import { useCallback, useEffect, useMemo } from 'react';

import { authRepository } from '@/repositories/auth-repository';
import { useCurrentConnection } from '@/hooks/auth/hooks/useCurrentConnection';
import { useWalletTransaction } from '@/hooks/auth';
import { useSuiWallet } from '@/hooks/auth/hooks/useSuiWallet';
import { useZkLoginWallet } from '@/hooks/auth/hooks/useZkLoginWallet';
import { ensureZkLoginJwtSynced } from '@/hooks/auth/hooks/useZkLoginJwtSync';
import { useWalletStore } from '@/hooks/auth/hooks/useWalletStore';
import type { WalletSliceKey } from '@/hooks/auth/types/wallet-slice.type';
import { clearCurrentAuthToken, setCurrentAuthToken, subscribeAuthTokenUpdates } from '../utils/auth-token-registry';
import { getWalletUniqueIdentifier, hasResolvableConnection, resolveWalletSliceKey } from '../utils/wallet.util';

const LOGIN_SIGNATURE_MESSAGE = 'Welcome to Suigar! Sign this message to unlock full chat access and enhanced profile features.';
const activeTokenInvalidations = new Set<string>();
let activeWalletAuthCreation: {
	walletId: string | null;
	promise: Promise<string | null> | null;
} = {
	walletId: null,
	promise: null,
};

export function useAuthToken() {
	const { account, authType, wallet } = useCurrentConnection();
	const { signPersonalMessage } = useWalletTransaction();
	const suiWallet = useSuiWallet();
	const zkLoginWallet = useZkLoginWallet();
	const setWalletAuthToken = useWalletStore((state) => state.setWalletAuthToken);

	const resolvedAddress = account?.address ?? null;
	const resolvedWalletSliceKey = useMemo<WalletSliceKey | null>(() => resolveWalletSliceKey(authType), [authType]);
	const activeWalletIdentifier = useMemo(() => (wallet ? (getWalletUniqueIdentifier(wallet) ?? null) : null), [wallet]);
	const walletConnectionAvailability = useMemo<Record<WalletSliceKey, boolean>>(
		() => ({
			suiWallet: hasResolvableConnection(suiWallet),
			zkLoginWallet: hasResolvableConnection(zkLoginWallet),
		}),
		[suiWallet, zkLoginWallet],
	);

	const currentWalletAuthToken = useMemo(() => {
		if (!resolvedWalletSliceKey) {
			return null;
		}

		return resolvedWalletSliceKey === 'zkLoginWallet' ? zkLoginWallet.authToken : suiWallet.authToken;
	}, [resolvedWalletSliceKey, suiWallet.authToken, zkLoginWallet.authToken]);

	const walletAuthTokens = useMemo(
		() =>
			(['suiWallet', 'zkLoginWallet'] as WalletSliceKey[])
				.map((walletType) => {
					const token = walletType === 'suiWallet' ? suiWallet.authToken : zkLoginWallet.authToken;
					return token ? { walletType, token } : null;
				})
				.filter((entry): entry is { walletType: WalletSliceKey; token: string } => Boolean(entry)),
		[suiWallet.authToken, zkLoginWallet.authToken],
	);

	const activeTokenWalletType = useMemo<WalletSliceKey | null>(() => {
		if (zkLoginWallet.currentWallet && zkLoginWallet.authToken) {
			return 'zkLoginWallet';
		}

		if (suiWallet.currentWallet && suiWallet.authToken) {
			return 'suiWallet';
		}

		return resolvedWalletSliceKey;
	}, [resolvedWalletSliceKey, suiWallet.authToken, suiWallet.currentWallet, zkLoginWallet.authToken, zkLoginWallet.currentWallet]);

	const jwtToken = useMemo(() => {
		if (activeTokenWalletType === 'zkLoginWallet') {
			return zkLoginWallet.authToken ?? null;
		}

		if (activeTokenWalletType === 'suiWallet') {
			return suiWallet.authToken ?? null;
		}

		return null;
	}, [activeTokenWalletType, suiWallet.authToken, zkLoginWallet.authToken]);

	useEffect(() => {
		setCurrentAuthToken(jwtToken, { walletType: activeTokenWalletType, silent: true });
	}, [activeTokenWalletType, jwtToken]);

	useEffect(() => {
		return subscribeAuthTokenUpdates((token, metadata) => {
			const walletType = metadata.walletType;
			if (!walletType) {
				return;
			}
			setWalletAuthToken(walletType, token ?? null);
		});
	}, [setWalletAuthToken]);

	const syncAuthToken = useCallback(
		(token: string, targetType?: WalletSliceKey) => {
			const walletType = targetType ?? resolvedWalletSliceKey ?? activeTokenWalletType;
			if (walletType) {
				setWalletAuthToken(walletType, token);
			}
			setCurrentAuthToken(token, { walletType, silent: true });
		},
		[activeTokenWalletType, resolvedWalletSliceKey, setWalletAuthToken],
	);

	const clearJwtToken = useCallback(
		(targetType?: WalletSliceKey) => {
			const walletType = targetType ?? activeTokenWalletType;
			if (walletType) {
				setWalletAuthToken(walletType, undefined);
			} else {
				setWalletAuthToken('suiWallet', undefined);
				setWalletAuthToken('zkLoginWallet', undefined);
			}
			clearCurrentAuthToken({ walletType, silent: true });
		},
		[activeTokenWalletType, setWalletAuthToken],
	);

	useEffect(() => {
		if (account || walletAuthTokens.length === 0) {
			return;
		}

		const tokensToInvalidate = walletAuthTokens.filter(({ walletType, token }) => {
			const connectionResolvable = walletConnectionAvailability[walletType];
			return !connectionResolvable && !activeTokenInvalidations.has(token);
		});
		if (!tokensToInvalidate.length) {
			return;
		}

		let cancelled = false;
		tokensToInvalidate.forEach(({ token }) => activeTokenInvalidations.add(token));

		const invalidateTokens = async () => {
			try {
				await Promise.all(
					tokensToInvalidate.map(async ({ token }) => {
						try {
							await authRepository.invalidateAuthToken(token);
						} catch (error) {
							console.warn('Failed to invalidate auth token after disconnection', error);
						}
					}),
				);
			} finally {
				tokensToInvalidate.forEach(({ token }) => activeTokenInvalidations.delete(token));
				if (!cancelled) {
					tokensToInvalidate.forEach(({ walletType }) => {
						clearJwtToken(walletType);
					});
				}
			}
		};

		void invalidateTokens();

		return () => {
			cancelled = true;
		};
	}, [account, clearJwtToken, walletAuthTokens, walletConnectionAvailability]);

	const requestLoginSignature = useCallback(async (): Promise<string | null> => {
		if (!authType || !resolvedWalletSliceKey) {
			throw new Error('Signature requests require an active wallet connection');
		}

		try {
			const signature = await signPersonalMessage(LOGIN_SIGNATURE_MESSAGE, resolvedWalletSliceKey);
			return signature;
		} catch (error) {
			const code = (error as { code?: number | string } | null)?.code;
			if (code === 4001 || code === '4001') {
				return null;
			}
			const lowerMessage = (error as { message?: string } | null)?.message?.toLowerCase() ?? '';
			if (lowerMessage.includes('reject') || lowerMessage.includes('denied') || lowerMessage.includes('cancel')) {
				return null;
			}
			throw error instanceof Error ? error : new Error('Failed to sign message');
		}
	}, [authType, resolvedWalletSliceKey, signPersonalMessage]);

	const requestSignedAuthToken = useCallback(async (): Promise<string | null> => {
		if (!authType || !resolvedAddress || !resolvedWalletSliceKey || !wallet) {
			throw new Error('Authentication context unavailable. Please login again.');
		}

		const signature = await requestLoginSignature();
		if (!signature) {
			setWalletAuthToken(resolvedWalletSliceKey, null);
			return null;
		}

		if (resolvedWalletSliceKey === 'zkLoginWallet') {
			await ensureZkLoginJwtSynced(wallet);
		}

		const { token } = await authRepository.createAuthToken({
			address: resolvedAddress,
			authType,
			signature,
		});

		syncAuthToken(token, resolvedWalletSliceKey);

		return token;
	}, [authType, requestLoginSignature, resolvedAddress, resolvedWalletSliceKey, setWalletAuthToken, syncAuthToken, wallet]);

	const issueAuthToken = useCallback(async (): Promise<string> => {
		const token = await requestSignedAuthToken();
		if (!token) {
			throw new Error('Signature required to authenticate.');
		}
		return token;
	}, [requestSignedAuthToken]);

	const ensureAuthToken = useCallback(async (): Promise<string> => {
		if (currentWalletAuthToken) {
			return currentWalletAuthToken;
		}
		return issueAuthToken();
	}, [currentWalletAuthToken, issueAuthToken]);

	const refreshAuthToken = useCallback(async (): Promise<string> => {
		if (!resolvedWalletSliceKey) {
			return issueAuthToken();
		}

		if (!currentWalletAuthToken) {
			return issueAuthToken();
		}

		try {
			const { token } = await authRepository.refreshAuthToken();
			syncAuthToken(token, resolvedWalletSliceKey);
			return token;
		} catch {
			setWalletAuthToken(resolvedWalletSliceKey, null);
			clearCurrentAuthToken({ walletType: resolvedWalletSliceKey, silent: true });
			return issueAuthToken();
		}
	}, [currentWalletAuthToken, issueAuthToken, resolvedWalletSliceKey, setWalletAuthToken, syncAuthToken]);

	useEffect(() => {
		if (resolvedWalletSliceKey !== 'suiWallet' && resolvedWalletSliceKey !== 'zkLoginWallet') {
			return;
		}

		if (!wallet || !resolvedAddress) {
			return;
		}

		const requiresAuthToken = resolvedWalletSliceKey === 'suiWallet' ? currentWalletAuthToken === undefined : !currentWalletAuthToken;
		if (!requiresAuthToken) {
			return;
		}

		if (!activeWalletIdentifier) {
			return;
		}

		if (activeWalletAuthCreation.promise && activeWalletAuthCreation.walletId === activeWalletIdentifier) {
			return;
		}

		let cancelled = false;

		const run = async () => {
			const promise = resolvedWalletSliceKey === 'zkLoginWallet' ? issueAuthToken() : requestSignedAuthToken();
			activeWalletAuthCreation = {
				walletId: activeWalletIdentifier,
				promise,
			};

			try {
				await promise;
			} catch (error) {
				if (resolvedWalletSliceKey !== 'zkLoginWallet' || !cancelled) {
					console.error('Failed to issue auth token for connected wallet', error);
				}
			} finally {
				if (activeWalletAuthCreation.promise === promise) {
					activeWalletAuthCreation = {
						walletId: null,
						promise: null,
					};
				}
			}
		};

		void run();

		if (resolvedWalletSliceKey === 'zkLoginWallet') {
			return () => {
				cancelled = true;
			};
		}
	}, [activeWalletIdentifier, currentWalletAuthToken, issueAuthToken, requestSignedAuthToken, resolvedAddress, resolvedWalletSliceKey, wallet]);

	return {
		jwtToken,
		clearJwtToken,
		syncAuthToken,
		requestLoginSignature,
		requestSignedAuthToken,
		issueAuthToken,
		ensureAuthToken,
		refreshAuthToken,
	};
}
